import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Collapse } from '@mui/material';
// hooks
import useActiveLink from '../../../hooks/useActiveLink';
import { isNavigationItemActive } from '../../../utils/navigation';
//
import NavItem from './NavItem';

// ----------------------------------------------------------------------

NavList.propTypes = {
  data: PropTypes.object,
  depth: PropTypes.number,
  hasChild: PropTypes.bool,
  onItemClick: PropTypes.func,
};

export default function NavList({ data, depth, hasChild, onItemClick = () => {} }) {
  const { pathname } = useLocation();

  const { active, isExternalLink } = useActiveLink(data?.path, !data?.exact);

  const isChildActive = useMemo(
    () => (hasChild ? isNavigationItemActive(pathname, data) : false),
    [data, hasChild, pathname]
  );

  const effectiveActive = hasChild ? isChildActive || active : active;

  const [open, setOpen] = useState(effectiveActive);

  useEffect(() => {
    if (effectiveActive) {
      setOpen(true);
      return;
    }

    handleClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, effectiveActive]);

  const handleToggle = () => {
    if (data.depth > 1) {
      setOpen(true)
    } else {
      setOpen(!open);
    }
    onItemClick?.(data);
  };

  const handleClose = () => {
    if (data.depth > 1) return
    setOpen(false);
  };

  return (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={open}
        active={active}
        isExternalLink={isExternalLink}
        onClick={handleToggle}
      />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} onItemClick={onItemClick} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

NavSubList.propTypes = {
  data: PropTypes.array,
  depth: PropTypes.number,
  onItemClick: PropTypes.func,
};

function NavSubList({ data, depth, onItemClick }) {
  return (
    <>
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          onItemClick={onItemClick}
        />
      ))}
    </>
  );
}
